import { FormattedMessage, useIntl } from 'react-intl'
import makeStyles from '@mui/styles/makeStyles'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import { Box, Grid, Typography } from '@mui/material'
import { getSponsoredChallengeTypeName } from 'utils/getChallengeTypeName'
import { selectLocale } from 'store/locale/slice'
import { useSelector } from 'react-redux'
import { S3_URL } from 'config'
import { rem } from 'theme/materialUITheme'
import { ChallengeComputedState } from 'utils/challenges'

const useStyles = makeStyles(() => ({
  pageHeader: {
    display: 'flex',
    height: rem(170),
    width: '100%',
    backgroundColor: '#047448',
    borderRadius: rem(16),
    marginLeft: rem(10),
    marginRight: rem(10),
    padding: rem(20),
  },
  infoContainer: {
    display: 'flex',
    height: '100%',
    width: '100%',
    fontFamily: 'Söhne',
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: rem(20),
  },
  image: {
    width: rem(115),
    height: rem(115),
    borderRadius: '50%',
    border: '3px solid #fff',
    backgroundColor: '#fff',
    marginTop: '-10px',
  },
  basicInfoList: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: rem(16),
    lineHeight: rem(18),
    color: '#ECECEC',
  },
  challengeTitle: {
    height: rem(40),
    fontSize: rem(30),
    fontWeight: 600,
    color: '#fff',
    flexBasis: '100%',
    margin: `${rem(10)} 0 ${rem(5)}`,
  },
  daysRemaining: {
    position: 'absolute',
    bottom: 0,
    margin: `0 0 0 ${rem(10)}`,
  },
  timeRemaining: {
    color: '#ECECEC',
    marginBottom: '-5px',
  },
  timeRemainingText: {
    display: 'inline-block',
    fontSize: '0.9em',
  },
}))

interface Props {
  challenge: Challenge
  status: string
}

const PageHeader = ({ challenge, status }: Props) => {
  const classes = useStyles()
  const intl = useIntl()
  const locale = useSelector(selectLocale)

  const startDate = challenge.schedule?.startAt
    ? new Intl.DateTimeFormat(locale.language.code, { timeZone: locale.timeZone }).format(
        new Date(challenge.schedule.startAt),
      )
    : null
  const endDate = challenge.schedule?.endAt
    ? new Intl.DateTimeFormat(locale.language.code, { timeZone: locale.timeZone }).format(
        new Date(challenge.schedule.endAt),
      )
    : null

  const calculateDaysLeft = () => {
    if (challenge.computedState === ChallengeComputedState.LIVE && challenge.schedule?.endAt) {
      const diffInMs = new Date(challenge.schedule.endAt).getTime() - new Date().getTime()
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24)
      return Math.round(diffInDays)
    }
    return
  }

  if (status === 'loading') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={200}>
        <LoadingSpinner />
      </Box>
    )
  }

  return (
    <Box className={classes.pageHeader}>
      <Box className={classes.infoContainer}>
        <Box className={classes.imageContainer}>
          <img
            className={classes.image}
            src={challenge.media?.logoImageId ? `${S3_URL}challenge/${challenge.media.logoImageId}` : ''}
          />
          <Grid container direction="column" style={{ marginLeft: rem(20) }}>
            <div>
              <Typography component={'h2'} className={classes.challengeTitle}>
                {challenge.info?.name}
              </Typography>
            </div>
            <Box className={classes.basicInfoList}>
              <div>
                <FormattedMessage id="challenges.type" />:{' '}
                {getSponsoredChallengeTypeName(challenge.typeConfig?.type ?? '', intl)}
              </div>
              <div>
                <FormattedMessage id="challenges.target" />: {challenge.typeConfig?.target}
              </div>
              <div>
                <FormattedMessage id="strings.whereCanBePerformed" />:{' '}
                {challenge.roundEligibility?.countries?.join(', ')}
              </div>
              <div>
                <FormattedMessage id="strings.visibility" />:{' '}
                {challenge.audienceControl?.joinAudience?.countries?.join(', ')}
              </div>
              <div>
                <FormattedMessage id="strings.duration" />: {startDate && `${startDate}-`}
                {endDate && endDate}
                {challenge.computedState === ChallengeComputedState.LIVE && calculateDaysLeft() && (
                  <>
                    {', '}
                    {calculateDaysLeft()} <FormattedMessage id="strings.daysRemaining" />
                  </>
                )}
              </div>
            </Box>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}

export default PageHeader
